import { Info } from 'lucide-react'
import { useParams } from 'react-router'
import { useCallback, useEffect, useState } from 'react'

import Loading from '@/elements/Loading'
import Flex from '@/elements/Flex'
import Text from '@/elements/Text'
import Button from '@/elements/Button'
import ExclusionWords from '@/components/Organization/OrganizationInformation/ExclusionWords'
import Modal from '@/components/Modal'
import useDeactivateUser from '@/hooks/useDeactivateUser'
import useBreakpoints from '@/hooks/useBreakpoints'
import useOrganizationIndividualForm from '@/hooks/forms/useOrganizationIndividualForm'
import OrganizationIndividualForm from '@/components/Organization/OrganizationIndividuals/OrganizationIndividualForm'
import { colors } from '@/utils/theme'
import toast from '@/utils/toast'
import { useGetPrivateSubject } from '@/api/profile'
import { useGetOrgUserInfo } from '@/api/user'
import {
  createIndividualExclusionWord,
  deleteIndividualExclusionWord,
  updateOrganizationIndividual,
  useGetIndividualAlias,
  useGetIndividualExclusionWords,
  useGetIndividualSocialPlatforms
} from '@/api/organizationIndividual'

import IndividualSocialChannels from './IndividualSocialChannels'
import IndividualAdditionalImages from './IndividualAdditionalImages'
import AdditionalImagesInfo from './AdditionalImagesInfo'
import IndividualAlias from './IndividualAlias'

function IndividualPersonalDetails() {
  const { isTablet, isMobile } = useBreakpoints()
  const [showExclusionWordsInfo, setShowExclusionWordsInfo] = useState(false)
  const [showAdditionalImagesInfo, setShowAdditionalImagesInfo] = useState(false)

  const { id: individualIdParam } = useParams<{ id: string }>()
  const individualId = individualIdParam || ''

  const { data: orgUserInfo } = useGetOrgUserInfo({ subjectId: individualId })
  const orgIndividualId = orgUserInfo?.data?.organizationIndividual?.id || ''

  const {
    data: individualResponse,
    isLoading: isLoadingIndividual,
    refetch: refetchIndividual
  } = useGetPrivateSubject(individualId, true)
  const individualData = individualResponse?.data || null
  const { form, resetForm } = useOrganizationIndividualForm(individualData)
  const { DeactivateUserModal } = useDeactivateUser({
    userType: 'individual',
    userId: orgIndividualId ?? '',
    refetch: refetchIndividual,
    redirect: true
  })

  useEffect(() => {
    resetForm(individualData)
  }, [individualData, resetForm])

  const { data: aliasData, refetch: refetchAlias } = useGetIndividualAlias(individualId)

  const submitUpdateIndividual = useCallback(async () => {
    const isValid = await form.trigger()
    if (isValid && orgIndividualId) {
      const dirtyFields = form.formState.dirtyFields
      const values = form.getValues()
      const formData = new FormData()

      Object.entries({
        ...(dirtyFields.avatar && {
          avatarAction: values.avatar ? 'update' : 'delete',
          ...(values.avatar ? { avatar: values.avatar } : {})
        }),
        ...(dirtyFields.cover && {
          coverAction: values.cover ? 'update' : 'delete',
          ...(values.cover ? { cover: values.cover } : {})
        }),
        ...(dirtyFields.firstName && { firstName: values.firstName }),
        ...(dirtyFields.lastName && { lastName: values.lastName || '' }),
        ...(dirtyFields.city && { city: values.city || '' }),
        ...(dirtyFields.state && { state: values.state || '' }),
        ...(dirtyFields.groupName && { groupName: values.groupName || '' }),
        ...(dirtyFields.specialty && { specialty: values.specialty || '' }),
      }).forEach(([key, value]) => formData.append(key, value))

      try {
        await updateOrganizationIndividual(orgIndividualId, formData)
        refetchIndividual()
        refetchAlias()
        toast.success('Individual updated successfully!')
      } catch {
        toast.error('Failed to update individual')
      }
    }
  }, [form, orgIndividualId, refetchAlias, refetchIndividual])

  const {
    data: socialChannelsData,
    refetch: refetchSocialChannels,
    isLoading: isLoadingSocialChannels
  } = useGetIndividualSocialPlatforms({ subjectId: individualId })
  const officialSocialChannels = socialChannelsData?.data || null
  const {
    data: trustedSocialChannelsData,
    refetch: refetchTrustedSocialChannels,
    isLoading: isLoadingTrustedSocialChannels
  } = useGetIndividualSocialPlatforms({ subjectId: individualId, trusted: true })
  const trustedSocialChannels = trustedSocialChannelsData?.data || null

  const { data: exclusionWordsData, refetch: refetchExclusionWords } = useGetIndividualExclusionWords({ subjectId: individualId })
  const exclusionWords = exclusionWordsData?.data?.exclusionWords || []

  const handleExclusionWords = useCallback((action: 'add' | 'remove') => {
    return async (data: string | { word: string }) => {
      if (action === 'add') {
        await createIndividualExclusionWord(individualId, data as { word: string })
      } else if (action === 'remove') {
        await deleteIndividualExclusionWord(individualId, data as string)
      }
    }
  }, [individualId])

  return (
    <Flex
      wfull
      className='card'
      align='flex-end'
      gap={8}
      p={isMobile ? 3 : isTablet ? 5 : 7}
    >
      {isLoadingIndividual ? (
        <Flex align='center' justify='center' wfull py={8}>
          <Loading />
        </Flex>
      ) : (
        <>
          <Flex wfull inline={!isTablet} gap={4} wrap>
            <Flex fraction={4} gap={2} wfull>
              <Text variant='h3'>Personal details</Text>
              <Text color='black-light-40'>
                Update individual's personal information and profile details.
              </Text>
            </Flex>
            <Flex wfull fraction={6} gap={4} align='end'>
              <OrganizationIndividualForm form={form} mode='edit' onSubmit={submitUpdateIndividual} />
            </Flex>
          </Flex>

          {/* <Flex color='gray-light-20' wfull style={{ height: '1px' }} />

          <Flex wfull inline={!isTablet} gap={4} wrap>
            <Flex fraction={4} gap={2} wfull>
              <Text variant='h3'>Additional details</Text>
              <Text color='black-light-40'>
                Update individual's additional information.
              </Text>
            </Flex>
            <Flex fraction={6} wfull hfull>
              <IndividualAdditionalDetails subjectId={individualId} />
            </Flex>
          </Flex> */}
          <Flex color='gray-light-20' wfull style={{ height: '1px' }} />

          <Flex wfull inline={!isTablet} gap={4} wrap>
            <Flex fraction={4} gap={2} wfull>
              <Text variant='h3'>Aliases and Nicknames (Optional)</Text>
              <Text color='black-light-40'>
                Add aliases or nicknames you use online. Watchdog will alert you when these names appear on untrusted sources.
                <br /><br />
                Results may vary.
              </Text>
            </Flex>
            <Flex fraction={6} wfull hfull>
              <IndividualAlias
                individualId={individualId}
                alias={aliasData?.data?.alias || []}
                refetchAlias={refetchAlias}
              />
            </Flex>
          </Flex>

          <Flex color='gray-light-20' wfull style={{ height: '1px' }} />

          <Flex wfull inline={!isTablet} gap={4} wrap>
            <Flex fraction={4} gap={2} wfull>
              <Flex inline gap={2}>
                <Text variant='h3'>Exclude Alerts by Context (Optional)</Text>
                <Flex mt={1}>
                  <Button variant='textOnly' onClick={() => setShowExclusionWordsInfo(true)}>
                    <Info
                      size={24}
                      style={{
                        minWidth: 24,
                        minHeight: 24,
                      }}
                    />
                  </Button>
                  {showExclusionWordsInfo && (
                    <Modal
                      title={<Info size={24} color={colors['black']} />}
                      isOpen={showExclusionWordsInfo}
                      onCancel={() => setShowExclusionWordsInfo(false)}
                      style={{
                        width: 642,
                      }}
                    >
                      <Flex gap={3}>
                        <Text variant='h4' color='black'>Use this to filter out alerts tied to unrelated industries, sports, or public topics that don't apply to you</Text>
                        <Text variant='textblock'>For example, if you share a name with a prominent real estate agent, you can add keywords like “real estate agent,” “open house,” or “realtor.” Watchdog will suppress alerts where your name or aliases appear in these contexts.</Text>
                      </Flex>
                    </Modal>
                  )}
                </Flex>
              </Flex>
              <Text color='black-light-40'>
                Select contexts that are not related to you but may appear alongside your name or aliases online.
                When selected, Watchdog will suppress alerts where your name or aliases appear in these contexts.
                This helps reduce alerts caused by people with the same name or by unrelated topics.
              </Text>
            </Flex>
            <Flex fraction={6} wfull hfull>
              <ExclusionWords
                individualKeywords={exclusionWords}
                refetchIndividualKeywords={refetchExclusionWords}
                handleCreate={handleExclusionWords('add')}
                handleDelete={handleExclusionWords('remove')}
              />
            </Flex>
          </Flex>

          <Flex color='gray-light-20' wfull style={{ height: '1px' }} />

          <Flex wfull inline={!isTablet} gap={4} wrap>
            <Flex fraction={4} gap={2} wfull>
              <Flex inline align='center' gap={2}>
                <Text variant='h3'>Headshots</Text>
                <Button variant='textOnly' onClick={() => setShowAdditionalImagesInfo(true)}>
                  <Info
                    size={24}
                    style={{
                      minWidth: 24,
                      minHeight: 24,
                    }} />
                </Button>
                {showAdditionalImagesInfo && (
                  <AdditionalImagesInfo
                    show={showAdditionalImagesInfo}
                    setShow={setShowAdditionalImagesInfo}
                  />
                )}
              </Flex>
              <Text color='black-light-40'>
                Start by uploading a high-quality headshot. This will serve as your reference photo for Watchdog's image search. Once your first image is uploaded, you can add up to 2 more headshots to enhance identification accuracy across the web.
              </Text>
            </Flex>
            <Flex fraction={6} wfull hfull>
              <IndividualAdditionalImages individualId={individualId} />
            </Flex>
          </Flex>

          <Flex color='gray-light-20' wfull style={{ height: '1px' }} />

          <Flex wfull inline={!isTablet} gap={4} wrap>
            <Flex fraction={4} gap={2} wfull>
              <Text variant='h3'>Official Social Media Accounts & Websites</Text>
              <Text color='black-light-40'>
                Add your official social media accounts and personal or professional websites.
                <br /><br />
                Content from these sources will be excluded from alerts, since you control them.
              </Text>
            </Flex>
            <Flex fraction={6} wfull hfull>
              <IndividualSocialChannels
                individualId={individualId}
                socialChannels={officialSocialChannels}
                refetchSocialChannels={refetchSocialChannels}
                isLoading={isLoadingSocialChannels}
              />
            </Flex>
          </Flex>

          <Flex color='gray-light-20' wfull style={{ height: '1px' }} />

          <Flex wfull inline={!isTablet} gap={4} wrap>
            <Flex fraction={4} gap={2} wfull>
              <Text variant='h3'>Trusted Third-Party Sources</Text>
              <Text color='black-light-40'>
                Add social media accounts or websites that do not belong to you but are trusted sources.
                <br /><br />
                Content from these sources will be excluded from alerts.
              </Text>
            </Flex>
            <Flex fraction={6} wfull hfull>
              <IndividualSocialChannels
                isTrustedThirdParty
                individualId={individualId}
                socialChannels={trustedSocialChannels}
                refetchSocialChannels={refetchTrustedSocialChannels}
                isLoading={isLoadingTrustedSocialChannels}
              />
            </Flex>
          </Flex>

          <Flex color='gray-light-20' wfull style={{ height: '1px' }} />

          <Flex wfull inline align='center' gap={4} wrap>
            <Flex fraction={4} gap={2} wfull>
              <Text variant='h3'>Other actions</Text>
            </Flex>
            <Flex fraction={6} wfull align='end' hfull>
              <DeactivateUserModal>
                <Button
                  variant='danger'
                  styles={{ border: 0, whiteSpace: 'nowrap' }}
                >
                  Deactivate User
                </Button>
              </DeactivateUserModal>
            </Flex>
          </Flex>
        </>
      )}
    </Flex>
  )
}

export default IndividualPersonalDetails
